/**
 * USGS/EPA watershed + aquifer identity — fills the previously-all-null
 * `waterResource` SED section with real structured facts.
 *
 * RD-1 (D): refdata-ONLY, over two datasets:
 *   `wbd-huc`   — USGS Watershed Boundary Dataset HUC8/10/12 polygons
 *                 (a `level` prop distinguishes them)
 *   `aquifers`  — USGS principal aquifers + EPA sole-source aquifers
 *                 (a `kind` prop distinguishes them)
 *
 * The section's honest nulls stay null: consumptive use, water rights,
 * depth-to-water, and NPDES ids still have no in-repo source, and the
 * disclaimer keeps saying so.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL } from './types';
import type { WaterResourceEvidence } from '../../types/siteEvidence';
import { refdataContains, tryRefdata } from '../refdata/refdataClient';
import type { RefdataFeature } from '../../types/refdata';

function hucOf(features: RefdataFeature[], level: string): { code: string; name: string | null } | null {
  const feature = features.find(f => String(f.props.LEVEL ?? '') === level);
  if (!feature) return null;
  const code = typeof feature.props.HUC === 'string' ? feature.props.HUC
    : typeof feature.props.HUC === 'number' ? String(feature.props.HUC) : '';
  if (code === '') return null;
  const name = typeof feature.props.NAME === 'string' && feature.props.NAME !== '' ? feature.props.NAME : null;
  return { code, name };
}

function aquiferOf(features: RefdataFeature[], kind: string): { name: string } | null {
  const feature = features.find(f => String(f.props.KIND ?? '') === kind);
  if (!feature) return null;
  const name = typeof feature.props.NAME === 'string' && feature.props.NAME !== '' ? feature.props.NAME : null;
  return name ? { name } : null;
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const USGS_WATERS_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

export const usgsWatersProvider: DataProvider<WaterResourceEvidence> = {
  id: 'usgs-waters',
  name: 'USGS Watersheds & Aquifers',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${USGS_WATERS_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat, lng, _apiKey?: string, _geo?: ProviderGeoContext): Promise<ProviderResult<WaterResourceEvidence>> {
    const huc = await tryRefdata('wbd-huc', async () => {
      const res = await refdataContains('wbd-huc', { lat, lng });
      return res ? { features: res.features, vintage: res.vintageDate } : null;
    });
    if (!huc) {
      throw new Error('USGS WBD: refdata dataset not available — seed `wbd-huc` (npm run refdata:ingest:wbd)');
    }
    // Aquifers are optional enrichment — a missing aquifer dataset degrades
    // to nulls rather than failing the whole section.
    const aquifers = await tryRefdata('aquifers', async () => {
      const res = await refdataContains('aquifers', { lat, lng });
      return res ? { features: res.features } : null;
    });

    const huc8 = hucOf(huc.features, '8');
    const huc10 = hucOf(huc.features, '10');
    const huc12 = hucOf(huc.features, '12');
    const principalAquifer = aquifers ? aquiferOf(aquifers.features, 'principal') : null;
    const soleSourceAquifer = aquifers ? aquiferOf(aquifers.features, 'sole-source') : null;

    const data: WaterResourceEvidence = {
      consumptiveUseAfy: null,
      waterRightsStatus: null,
      aquiferDepthToWaterFt: null,
      npdesPermitId: null,
      waterResourceBasis: 'provider',
      provenance: 'provider',
      disclaimer: 'Watershed and aquifer identity from federal snapshots (USGS WBD'
        + (aquifers ? ', USGS principal aquifers, EPA sole-source aquifers' : '')
        + '). Consumptive-use allotments, water-rights status, depth-to-water, and NPDES permits '
        + 'still require the state water-rights database, USGS NWIS, and EPA ECHO respectively.',
      huc8,
      huc10,
      huc12,
      principalAquifer,
      soleSourceAquifer,
    };

    const hucLabel = huc12?.code ?? huc10?.code ?? huc8?.code ?? 'no watershed matched';
    return {
      data,
      sources: [{
        field: 'waterResource',
        source: `USGS Watershed Boundary Dataset (local snapshot, vintage ${huc.vintage}) — HUC ${hucLabel}`
          + (soleSourceAquifer ? `; EPA sole-source aquifer: ${soleSourceAquifer.name}` : '')
          + (principalAquifer ? `; principal aquifer: ${principalAquifer.name}` : ''),
        url: 'https://www.epa.gov/waterdata/waters-watershed-assessment-tracking-environmental-results-system',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
